import { inject, Injectable, Injector } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { AuthService } from "../auth/auth.service";
import { LoginDetailsStorageService } from "./login-details-storage.service";
import { TempDetailsStorageService } from "./temp-details-storage.service";
import { IndicationDetailsStorageService } from "./indication-details-storage.service";
import { QuoteDetailsStorageService } from "./quote-details-storage.service";
import { PolicyDetailsStorageService } from "./policy-details-storage.service";

export interface SessionCacheHydrationResult {
  loginRestored: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SessionCacheHydrationService {
  private readonly injector = inject(Injector);
  private readonly loginDetailsStorage = inject(LoginDetailsStorageService);
  private readonly tempDetailsStorage = inject(TempDetailsStorageService);
  private readonly indicationDetailsStorage = inject(IndicationDetailsStorageService);
  private readonly quoteDetailsStorage = inject(QuoteDetailsStorageService);
  private readonly policyDetailsStorage = inject(PolicyDetailsStorageService);

  private hydrationResult: SessionCacheHydrationResult | null = null;
  private restoreInFlight: Observable<SessionCacheHydrationResult> | null = null;

  isHydrated(): boolean {
    return this.hydrationResult !== null;
  }

  getHydrationResult(): SessionCacheHydrationResult | null {
    return this.hydrationResult;
  }

  /** After fresh login/OTP when caches were written in-memory (no GET needed yet). */
  markHydrated(loginRestored = true): void {
    this.hydrationResult = { loginRestored };
    this.restoreInFlight = null;
  }

  /** Resolves after login + workflow caches are loaded from API (deduped across callers). */
  ensureRestored(): Observable<SessionCacheHydrationResult> {
    if (this.hydrationResult) {
      return of(this.hydrationResult);
    }

    if (this.restoreInFlight) {
      return this.restoreInFlight;
    }

    if (!this.loginDetailsStorage.getItem("token")) {
      this.loginDetailsStorage.restoreAccessTokenFromBootstrapStorage();
    }

    const startRestore$ = this.loginDetailsStorage.getItem("token")
      ? of(true)
      : this.injector.get(AuthService).bootstrapSessionFromRefreshToken();

    const restore$ = startRestore$.pipe(
      switchMap((canRestore) => {
        if (!canRestore || !this.loginDetailsStorage.getItem("token")) {
          const failed: SessionCacheHydrationResult = { loginRestored: false };
          this.hydrationResult = failed;
          return of(failed);
        }
        return this.restoreAllFromApi();
      }),
      tap((result: SessionCacheHydrationResult) => {
        this.hydrationResult = result;
      }),
      catchError(() => {
        const failed: SessionCacheHydrationResult = { loginRestored: false };
        this.hydrationResult = failed;
        return of(failed);
      }),
      shareReplay(1)
    );

    this.restoreInFlight = restore$;
    return restore$;
  }

  /** Call on logout so the next login/refresh triggers a fresh restore. */
  reset(): void {
    this.hydrationResult = null;
    this.restoreInFlight = null;
  }

  private restoreAllFromApi(): Observable<SessionCacheHydrationResult> {
    return this.loginDetailsStorage.loadFromCache().pipe(
      switchMap((loginRestored) =>
        this.tempDetailsStorage.loadFromCache().pipe(
          switchMap(() => this.indicationDetailsStorage.loadFromCache()),
          switchMap(() => this.quoteDetailsStorage.loadFromCache()),
          switchMap(() => this.policyDetailsStorage.loadFromCache()),
          map(() => ({ loginRestored }))
        )
      )
    );
  }
}
